import type { SSEMessage } from '@/_shared/types';

import type { ActivityItem } from '../components/tool-call-meta';
import {
  DASHBOARD_PREVIEW_TOOL,
  DASHBOARD_SAVE_TOOL,
  DASHBOARD_UPDATE_TOOL,
  dashboardToolCallSlug,
  isSuccessfulPreviewResult,
  parseDashboardPersistResult,
  parseDashboardToolResultSummary,
  parseSaveDashboardResult,
  previewArgsToDraft,
} from './dashboard-draft';
import type { ResolvedDashboardArtifact } from './resolved-dashboard-artifact';

/** Slugs already persisted before each message index (from save/update tool results). */
export function buildDashboardSavedSlugsPrefix(messages: SSEMessage[]): Set<string>[] {
  const prefix: Set<string>[] = Array.from({ length: messages.length + 1 });
  const pendingPersistToolIds = new Set<string>();
  // Copy-on-write: reuse the same Set reference across indices where nothing was added — consumers
  // copy before mutating, so sharing is safe and avoids an O(n·S) rebuild on long transcripts.
  let current = new Set<string>();
  prefix[0] = current;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]!;
    if (msg.type === 'tool_use' && (msg.name === DASHBOARD_SAVE_TOOL || msg.name === DASHBOARD_UPDATE_TOOL)) {
      pendingPersistToolIds.add(msg.id);
    } else if (msg.type === 'tool_result' && pendingPersistToolIds.has(msg.id)) {
      pendingPersistToolIds.delete(msg.id);
      const ref = parseDashboardPersistResult(msg.content);
      if (ref && !current.has(ref.slug)) {
        current = new Set(current);
        current.add(ref.slug);
      }
    }
    prefix[i + 1] = current;
  }

  return prefix;
}

function withFriendlyName(item: ActivityItem, friendlyName: string, action: string): ActivityItem {
  return {
    ...item,
    toolUse: { ...item.toolUse, friendlyName, action },
  };
}

/**
 * Collapse preview+save dashboard tool rows into a single user-visible step.
 * - First build: "Preview dashboard" (preview only) or "Save dashboard" (preview+save merged).
 * - Updates to an already-saved slug: "Update dashboard" (save row hidden).
 */
export function normalizeDashboardActivityItems(items: ActivityItem[], savedSlugsBefore: Set<string>): ActivityItem[] {
  const savedSoFar = new Set(savedSlugsBefore);
  const result: ActivityItem[] = [];
  let i = 0;

  while (i < items.length) {
    const item = items[i]!;

    if (item.toolUse.name === DASHBOARD_UPDATE_TOOL) {
      result.push(withFriendlyName(item, 'Update dashboard', 'update_dashboard'));
      const ref = parseDashboardPersistResult(item.toolResult?.content ?? '');
      if (ref) savedSoFar.add(ref.slug);
      i += 1;
      continue;
    }

    if (item.toolUse.name === DASHBOARD_PREVIEW_TOOL) {
      // The slug, not the full draft: a panel-patch call (ACT2-1078) carries no spec in its arguments.
      const slug = dashboardToolCallSlug(item.toolUse.args);
      const ok = slug !== null && !!item.toolResult && isSuccessfulPreviewResult(item.toolResult.content);
      if (!ok) {
        result.push(item);
        i += 1;
        continue;
      }

      const next = items[i + 1];
      const adjacentSave =
        next?.toolUse.name === DASHBOARD_SAVE_TOOL &&
        !!next.toolResult &&
        parseSaveDashboardResult(next.toolResult.content)?.slug === slug;

      const isUpdate = savedSoFar.has(slug);
      const friendlyName = isUpdate ? 'Update dashboard' : adjacentSave ? 'Save dashboard' : 'Preview dashboard';
      const action = isUpdate ? 'update_dashboard' : adjacentSave ? 'save_dashboard' : 'preview_dashboard';

      result.push(withFriendlyName(item, friendlyName, action));

      if (adjacentSave) {
        savedSoFar.add(slug);
        i += 2;
      } else {
        i += 1;
      }
      continue;
    }

    if (item.toolUse.name === DASHBOARD_SAVE_TOOL) {
      const ref = parseSaveDashboardResult(item.toolResult?.content ?? '');
      // Redundant agent/client save on an already-live slug — auto-save handles updates.
      if (ref && savedSoFar.has(ref.slug)) {
        i += 1;
        continue;
      }
      result.push(withFriendlyName(item, 'Save dashboard', 'save_dashboard'));
      if (ref) savedSoFar.add(ref.slug);
      i += 1;
      continue;
    }

    result.push(item);
    i += 1;
  }

  return result;
}

/** One launcher card per slug — the latest preview/save in the activity group. */
export interface DashboardActivityCard {
  slug: string;
  title: string;
  saved: boolean;
  isDraft: boolean;
  dashboardId?: string;
  /** From the preview/update spec; omitted when the payload carried none. */
  panelCount?: number;
}

function panelCountOf(
  draft: { spec: { panels: unknown[] } } | null,
  resolved: ResolvedDashboardArtifact | null | undefined,
): number | undefined {
  // The live count wins whenever it is known, INCLUDING zero. The producer omits it for an un-hydrated
  // store shell, so `undefined` means "unknown" and `0` means a genuinely empty dashboard.
  if (typeof resolved?.panelCount === 'number') return resolved.panelCount;
  return draft?.spec.panels.length;
}

export function dashboardArtifactCardsFromItems(
  items: ActivityItem[],
  resolveDashboardArtifact?: (slug: string) => ResolvedDashboardArtifact | null,
): DashboardActivityCard[] {
  const bySlug = new Map<string, DashboardActivityCard>();

  for (const item of items) {
    // A panel-patch call (ACT2-1078) has no spec in its arguments, so the card keys on the slug and
    // takes the title and panel count from the live artifact, a full call's args, the call's own result
    // (which survives a reload that cannot rebuild the artifact), or an earlier card.
    if (item.toolUse.name === DASHBOARD_UPDATE_TOOL) {
      if (!item.toolResult || !isSuccessfulPreviewResult(item.toolResult.content)) continue;
      const slug = dashboardToolCallSlug(item.toolUse.args);
      const saved = parseDashboardPersistResult(item.toolResult.content);
      if (!slug || !saved) continue;
      const draft = previewArgsToDraft(item.toolUse.args);
      const resolved = resolveDashboardArtifact?.(slug);
      const result = parseDashboardToolResultSummary(item.toolResult.content);
      const panelCount = panelCountOf(draft, resolved) ?? result.panelCount ?? bySlug.get(slug)?.panelCount;
      bySlug.set(slug, {
        slug,
        title: resolved?.title ?? draft?.title ?? result.title ?? bySlug.get(slug)?.title ?? slug,
        saved: resolved?.saved ?? true,
        isDraft: resolved?.isDraft ?? false,
        dashboardId: saved.id,
        ...(panelCount === undefined ? {} : { panelCount }),
      });
      continue;
    }

    if (item.toolUse.name === DASHBOARD_PREVIEW_TOOL) {
      if (!item.toolResult || !isSuccessfulPreviewResult(item.toolResult.content)) continue;
      const slug = dashboardToolCallSlug(item.toolUse.args);
      if (!slug) continue;
      const draft = previewArgsToDraft(item.toolUse.args);
      const resolved = resolveDashboardArtifact?.(slug);
      const result = parseDashboardToolResultSummary(item.toolResult.content);
      const panelCount = panelCountOf(draft, resolved) ?? result.panelCount ?? bySlug.get(slug)?.panelCount;
      bySlug.set(slug, {
        slug,
        title: resolved?.title ?? draft?.title ?? result.title ?? bySlug.get(slug)?.title ?? slug,
        saved: resolved?.saved ?? false,
        isDraft: resolved?.isDraft ?? true,
        ...(panelCount === undefined ? {} : { panelCount }),
      });
      continue;
    }

    if (item.toolUse.name === DASHBOARD_SAVE_TOOL) {
      if (!item.toolResult) continue;
      const saved = parseSaveDashboardResult(item.toolResult.content);
      if (!saved) continue;
      const draft = previewArgsToDraft(item.toolUse.args);
      const resolved = resolveDashboardArtifact?.(saved.slug);
      const panelCount = panelCountOf(draft, resolved);
      bySlug.set(saved.slug, {
        slug: saved.slug,
        title: resolved?.title ?? draft?.title ?? saved.slug,
        saved: resolved?.saved ?? true,
        isDraft: resolved?.isDraft ?? false,
        dashboardId: saved.id,
        ...(panelCount === undefined ? {} : { panelCount }),
      });
    }
  }

  return [...bySlug.values()];
}
